import { CopyOutlined } from "@gds-react/icons";
import { IconWrapper, Label, Wrapper } from "./CopyWrapper.styles";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState, useMemo } from "react";

interface Props {
  children: string;
  iconPosition?: "left" | "right";
  direction?: "ttb" | "btt" | "ltr" | "rtl";
}

export default function CopyWrapper({
  children,
  iconPosition = "right",
  direction = "ttb",
}: Props) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const timeout = useRef<number | any | null>(null);
  const [isCopying, setIsCopying] = useState(false);

  useEffect(() => {
    return () => {
      timeout.current && clearInterval(timeout.current);
    };
  }, []);

  const copyToClipboard = (event) => {
    setIsCopying(true);
    event.stopPropagation();
    navigator.clipboard.writeText(children);

    timeout.current = window.setTimeout(() => {
      setIsCopying(false);
    }, 1000);
  };

  const anim = useMemo(() => {
    if (direction === "btt") {
      return {
        initial: {
          y: -28,
          x: -18,
          opacity: 0,
        },
        final: {
          y: -36,
          x: -18,
          opacity: 1,
        },
        exit: {
          y: -28,
          x: -18,
          opacity: 0,
        },
      };
    } else if (direction === "ttb") {
      return {
        initial: {
          y: 8,
          x: -18,
          opacity: 0,
        },
        final: {
          y: 14,
          x: -18,
          opacity: 1,
        },
        exit: {
          y: 8,
          x: -18,
          opacity: 0,
        },
      };
    } else if (direction === "rtl") {
      return {
        initial: {
          y: -12,
          x: -42,
          opacity: 0,
        },
        final: {
          y: -12,
          x: -60,
          opacity: 1,
        },
        exit: {
          y: -12,
          x: -42,
          opacity: 0,
        },
      };
    } else if (direction === "ltr") {
      return {
        initial: {
          y: -12,
          x: 18,
          opacity: 0,
        },
        final: {
          y: -12,
          x: 30,
          opacity: 1,
        },
        exit: {
          y: -12,
          x: 18,
          opacity: 0,
        },
      };
    }
  }, [direction]);

  return (
    <Wrapper $iconPosition={iconPosition}>
      <span>{children}</span>
      <IconWrapper className="IconWrapper">
        <AnimatePresence mode="wait">
          {isCopying && (
            <motion.div
              variants={anim}
              initial="initial"
              animate="final"
              exit="exit"
              transition={{ duration: 0.2 }}
            >
              <Label>Copied!</Label>
            </motion.div>
          )}
        </AnimatePresence>
        <CopyOutlined
          style={{ width: 14, height: 14 }}
          onClick={copyToClipboard}
        />
      </IconWrapper>
    </Wrapper>
  );
}
